// src/services/avatarKitLoader.js — the catalogue and its parts, in the browser.
//
// The phone's src/avatar/kitCache.ts over fetch(): the catalogue index with a
// short memory life, every part parsed once and shared, an avatar assembled
// from cached parts in ~10 ms (services/avatarAssemble.js, a generated twin).
// The browser's HTTP cache keeps the content-hashed part files across visits.

import { catalogUrl, partUrl, resolveParts, specKey } from './avatarKit';
import { assembleAvatar } from './avatarAssemble';
import { parseAvatarGlb, landmarksOf } from './avatarScene';
import { hasAvatar } from './avatar3d';
import { BACKEND_URL } from './socket';

export const PART_MEM_MAX = 16;
export const CATALOG_TTL_MS = 60 * 60 * 1000;
export const MEM_MAX = 8;

let kitBase = BACKEND_URL;
export function setKitBase(url) { kitBase = url; }

let catalogMem = null;
export function getCatalog(force = false) {
  const now = Date.now();
  if (!force && catalogMem && now - catalogMem.at < CATALOG_TTL_MS) return catalogMem.p;
  const p = fetch(catalogUrl(kitBase), { mode: 'cors', cache: 'no-cache' })
    .then((r) => { if (!r.ok) throw new Error(`catalog ${r.status}`); return r.json(); })
    .then((cat) => { if (!cat || !Array.isArray(cat.parts)) throw new Error('catalog: malformed'); return cat; });
  catalogMem = { at: now, p };
  p.catch(() => { if (catalogMem && catalogMem.p === p) catalogMem = null; });
  return p;
}

const partMem = new Map();
export function loadPart(part) {
  const key = part.file;
  const hit = partMem.get(key);
  if (hit) { partMem.delete(key); partMem.set(key, hit); return hit; }
  const p = fetch(partUrl(kitBase, part), { mode: 'cors' })
    .then((r) => { if (!r.ok) throw new Error(`part ${part.id} ${r.status}`); return r.arrayBuffer(); })
    .then((buf) => parseAvatarGlb(buf));
  partMem.set(key, p);
  p.catch(() => { if (partMem.get(key) === p) partMem.delete(key); });
  while (partMem.size > PART_MEM_MAX) { const k = partMem.keys().next().value; if (k === undefined) break; partMem.delete(k); }
  return p;
}

export async function warmSpec(spec) {
  const cat = await getCatalog();
  await Promise.all(resolveParts(cat, spec).map((p) => loadPart(p)));
}

export async function assembleFromSpec(spec) {
  const cat = await getCatalog();
  const parts = resolveParts(cat, spec);
  const loaded = await Promise.all(parts.map((p) => loadPart(p)));
  const root = assembleAvatar(parts.map((part, i) => ({ part, root: loaded[i].root })), spec);
  return { root, landmarks: landmarksOf(root), bytes: loaded.reduce((a, l) => a + l.bytes, 0) };
}

/* the assembled figures, shared by every view on the page */
const models = new Map();
export function avatarCacheKey(a) { return a && a.spec ? `allo-${specKey(a.spec)}` : `allo-${(a && a.id) || 'none'}`; }
export function loadAvatarModel(avatar) {
  if (!hasAvatar({ avatar })) return Promise.reject(new Error('no avatar'));
  const key = avatarCacheKey(avatar);
  const hit = models.get(key);
  if (hit) { models.delete(key); models.set(key, hit); return hit; }
  const p = assembleFromSpec(avatar.spec);
  models.set(key, p);
  p.catch(() => { if (models.get(key) === p) models.delete(key); });
  while (models.size > MEM_MAX) { const k = models.keys().next().value; if (k === undefined) break; models.delete(k); }
  return p;
}
export function warmAvatar(avatar) { if (avatar && hasAvatar({ avatar })) loadAvatarModel(avatar).catch(() => {}); }
